/**
 * Resizes an image File to fit within maxDimension on its longest side,
 * re-encoding as JPEG at the given quality. Returns the original file
 * unchanged if it is already within bounds, or if it is not an image.
 *
 * HEIC files must be converted to JPEG before calling this function —
 * browsers cannot decode HEIC on a canvas. The upload modal handles this
 * via prepareFile() before staging, so by the time resizeImage is called
 * the file will always be a standard image format.
 *
 * Videos are never resized — return as-is.
 */
export async function resizeImage(
  file: File,
  maxDimension = 1920,
  quality = 0.75,
): Promise<File> {
  if (!file.type.startsWith('image/')) return file

  return new Promise((resolve) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      const { naturalWidth: w, naturalHeight: h } = img

      // Already within bounds — skip re-encoding
      if (w <= maxDimension && h <= maxDimension) {
        resolve(file)
        return
      }

      const scale = maxDimension / Math.max(w, h)
      const targetW = Math.round(w * scale)
      const targetH = Math.round(h * scale)

      const canvas = document.createElement('canvas')
      canvas.width = targetW
      canvas.height = targetH

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(file) // fallback: return original
        return
      }

      ctx.drawImage(img, 0, 0, targetW, targetH)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file) // fallback: return original
            return
          }
          // Keep the original filename but with .jpg extension
          const name = file.name.replace(/\.[^.]+$/, '.jpg')
          resolve(new File([blob], name, { type: 'image/jpeg' }))
        },
        'image/jpeg',
        quality,
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(file) // fallback: return original on error
    }

    img.src = objectUrl
  })
}
